import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {GlobalEventsMap} from '@gongxigames/framework/slot/events';
import {
    CoreDI,
    CurrencyFormatter,
    inject,
    Installer,
    LanguageService,
    ModuleIsEnabledFilter
} from '@gongxigames/framework/core';
import {
    ILogic,
    Message,
    SlotSettings
} from '@gongxigames/framework/slot';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {IPromotionLogic, ILogicPromotionFunctional} from '@gongxigames/framework/promotions';

export function isEnabledNonMinimalBetPopup(components: Installer): boolean {
    return components.getComponent<SlotSettings>(CoreDI.Settings).get('nonMinimalBetPopupEnabled') || false;
}

export class NonMinimalBetPopup extends Module<null, GlobalEventsMap> {
    public static readonly NAME: symbol = Symbol('NonMinimalBetPopup');

    public static readonly isEnabled: ModuleIsEnabledFilter = isEnabledNonMinimalBetPopup;

    @inject(CoreDI.LanguageService)
    protected _languageService!: LanguageService;

    @inject(CoreDI.CurrencyFormatter)
    protected _currencyFormatter!: CurrencyFormatter;

    private promotion!: IPromotionLogic;

    private isRestore = false;
    private isPlayerInformed = false;

    public init(): void {
        this.promotion = this.getLogic<ILogic & ILogicPromotionFunctional>().getPromotion();

        super.init();
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.on('General.Game.STARTED', this.onGameStarted.bind(this));
        this.on('General.Game.RESTORED', this.onGameRestored.bind(this));
        this.on('General.Flow.PAID_ROUND_ENDED', this.onPaidRoundEventEnded.bind(this));
        this.on('General.Promotion.Ended', this.onPromotionEnded.bind(this));
    }

    protected onGameStarted(): void {
        if (this.logic.nextAction === Action.SPIN && !this.isMinimalBet() && !this.isPromotion()) {
            this.showPopup();
        }
    }

    protected onGameRestored(): void {
        this.isRestore = true;
    }

    protected onPaidRoundEventEnded(): void {
        if (this.logic.nextAction === Action.SPIN && !this.isMinimalBet() && !this.isPromotion() && this.isRestore) {
            this.showPopup();
            this.isRestore = false;
        }
    }

    protected onPromotionEnded(): void {
        if (!this.isMinimalBet() && !this.isPlayerInformed) {
            this.showPopup();
        }
    }

    protected showPopup(): void {
        this.isPlayerInformed = true;
        this.getEventManager().emit('General.Message.SHOW', this.createNonMinimalBetPopup());
    }

    protected createNonMinimalBetPopup(): Message {
        const minimalBet = this._currencyFormatter.formatCents(this.logic.getWager()*this.logic.getBets()[0]);
        const message = this._languageService.get('notMinimalBet', {minimalBet});

        return {
            id: 'NON_MINIMAL_BET_POPUP',
            type: 'normal',
            title: {id: 'info'},
            message,
            buttons: [{
                title: {id: 'ok'},
                action: 'close_message'
            }]
        };
    }

    protected isMinimalBet(): boolean {
        const totalBet = this.logic.getTotalBet();
        const minimalBet = this.logic.getBets()[0];
        const wager = this.logic.getWager();

        return totalBet === minimalBet * wager;
    }

    protected isPromotion<PromotionLogic extends IPromotionLogic>(): boolean {
        return (this.promotion as PromotionLogic).hasPromotion();
    }
}
